/**
 * The "A new version is available" toast, with a handle to drag it out of the way.
 *
 * Rendered through sonner's `toast.custom` (lib/app-update.ts). The toast is sticky, so it can
 * sit over something the member is reading; the grip lets them move it aside without dismissing
 * it (Tim, 20 Oct 2026). The position is not remembered: it lives in this component's state and
 * is gone when the toast closes, so the next prompt appears in the usual corner.
 *
 * Sonner's own swipe-to-dismiss is switched off for this toast (`dismissible: false`), so a drag
 * moves it rather than throwing it away; the X closes it instead. The card is kept on screen
 * while dragging, so it can never be lost past an edge.
 */

import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { toast } from 'sonner';
import { GripVertical, X } from 'lucide-react';

interface UpdateToastProps {
  id: string | number;
  onReload: () => void;
}

/** How much of the card must stay inside the viewport on every side, in px. */
const KEEP_VISIBLE = 8;

export function UpdateToast({ id, onReload }: UpdateToastProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{
    pointerX: number;
    pointerY: number;
    startX: number;
    startY: number;
    /** How far the card may move each way before an edge would cut it off. */
    min: { x: number; y: number };
    max: { x: number; y: number };
  } | null>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const card = cardRef.current;
    if (!card) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = card.getBoundingClientRect();
    drag.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      startX: offset.x,
      startY: offset.y,
      min: { x: offset.x - (rect.left - KEEP_VISIBLE), y: offset.y - (rect.top - KEEP_VISIBLE) },
      max: {
        x: offset.x + (window.innerWidth - KEEP_VISIBLE - rect.right),
        y: offset.y + (window.innerHeight - KEEP_VISIBLE - rect.bottom),
      },
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
    setOffset({
      x: clamp(d.startX + e.clientX - d.pointerX, d.min.x, d.max.x),
      y: clamp(d.startY + e.clientY - d.pointerY, d.min.y, d.max.y),
    });
  };

  const endDrag = () => {
    drag.current = null;
  };

  return (
    <div
      ref={cardRef}
      style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      className="glass-pop flex w-[356px] max-w-[calc(100vw-2rem)] items-center gap-2 p-3 pl-1.5 text-foreground"
    >
      <button
        type="button"
        aria-label="Drag to move"
        title="Drag to move"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="flex h-11 w-7 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-[13px] font-semibold leading-[18px]">A new version is available</p>
        <p className="text-xs leading-4 text-muted-foreground">Reload to get the latest fixes.</p>
      </div>

      <button
        type="button"
        onClick={onReload}
        className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        Reload
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => toast.dismiss(id)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
